import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { TASK_CATEGORIES, STATUSES } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { buildTaskCategoryRelationParams } from './taskCategoryApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', String(values.status_id));
  fd.append('name', values.name ?? '');
  fd.append('description', values.description ?? '');
  if (values.parent_id) {
    fd.append('parent_id', String(values.parent_id));
  }
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

const CreateTaskCategory = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [parentLoading, setParentLoading] = useState(false);

  const [statusOptions, setStatusOptions] = useState([]);
  const [parentOptions, setParentOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [record, setRecord] = useState(null);

  const fetchStatuses = useCallback(async () => {
    setStatusLoading(true);
    try {
      let response;
      try {
        response = await api.get(STATUSES.BY_MODEL('TaskCategory'));
      } catch {
        try {
          response = await api.get(STATUSES.BY_MODEL('TaskCategory'));
        } catch {
          response = await api.get(STATUSES.LIST, { params: { paginate: 0 } });
        }
      }
      const list = extractList(response?.data);
      const arr = Array.isArray(list) ? list : [];
      setStatusOptions(
        arr.map((s) => ({
          id: s.id,
          label: statusLabel(s),
          raw: s,
        }))
      );
    } catch {
      toast.error('Could not load statuses');
      setStatusOptions([]);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchParentCategories = useCallback(async () => {
    setParentLoading(true);
    try {
      const { data } = await api.get(TASK_CATEGORIES.LIST, {
        params: {
          paginate: 0,
          ...buildTaskCategoryRelationParams(),
        },
      });
      const rawList = data?.data?.list ?? data?.list ?? extractList(data);
      const arr = Array.isArray(rawList) ? rawList : [];
      const excludeId = isEdit ? editId : null;
      setParentOptions(
        arr
          .filter((c) => c?.id && c.id !== excludeId)
          .map((c) => ({
            id: c.id,
            label: c.name ?? String(c.id),
            raw: c,
          }))
      );
    } catch {
      toast.warn('Could not load parent categories');
      setParentOptions([]);
    } finally {
      setParentLoading(false);
    }
  }, [isEdit, editId]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  useEffect(() => {
    fetchParentCategories();
  }, [fetchParentCategories]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setRecord(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(TASK_CATEGORIES.BY_ID(editId), {
          params: buildTaskCategoryRelationParams(),
        });
        const t = data?.data ?? data;
        if (cancelled || !t) return;
        setRecord(t);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load task category');
        navigate('/task-categories');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  useEffect(() => {
    if (!isEdit || !record) return;

    setName(record.name ?? '');
    setDescription(record.description ?? '');

    const st = record.status && typeof record.status === 'object' ? record.status : null;
    const sid = record.status_id ?? st?.id;
    if (sid) {
      if (st) {
        const opt = { id: sid, label: statusLabel(st), raw: st };
        setSelectedStatus(opt);
        setStatusOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusOptions.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }

    const par = record.parent && typeof record.parent === 'object' ? record.parent : null;
    const pid = record.parent_id ?? par?.id;
    if (pid) {
      if (par) {
        const opt = { id: pid, label: par.name ?? String(pid), raw: par };
        setSelectedParent(opt);
        setParentOptions((prev) => (prev.some((o) => o.id === pid) ? prev : [...prev, opt]));
      } else {
        const opt = parentOptions.find((o) => o.id === pid);
        setSelectedParent(opt ?? { id: pid, label: String(pid), raw: {} });
      }
    } else {
      setSelectedParent(null);
    }
  }, [isEdit, record, statusOptions, parentOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStatus?.id || !name.trim()) {
      toast.warn('Select status and enter a name');
      return;
    }

    const values = {
      status_id: selectedStatus.id,
      parent_id: selectedParent?.id ?? '',
      name: name.trim(),
      description: description.trim(),
    };
    const fd = buildFormData(values);
    const q = new URLSearchParams(buildTaskCategoryRelationParams()).toString();

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${TASK_CATEGORIES.BY_ID(editId)}?${q}`, fd);
        toast.success('Task category updated');
      } else {
        await api.post(`${TASK_CATEGORIES.LIST}?${q}`, fd);
        toast.success('Task category created');
      }
      navigate('/task-categories');
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          (isEdit ? 'Failed to update task category' : 'Failed to create task category')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/task-categories')} sx={{ mb: 2 }}>
          Back to task categories
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit task category' : 'Create task category'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(_, v) => setSelectedStatus(v)}
                  loading={statusLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Status" required fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={parentOptions}
                  value={selectedParent}
                  onChange={(_, v) => setSelectedParent(v)}
                  loading={parentLoading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Parent category" placeholder="Optional" fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    'Save changes'
                  ) : (
                    'Create task category'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/task-categories')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateTaskCategory;
